import React from "react";
import { TextField, Tooltip, Box } from "@mui/material";
import { useFormikContext } from "formik";

export interface GenericTextFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  tooltip?: string;
  type?: string;
  required?: boolean;
  size?: "small" | "medium";
}

const GenericTextField: React.FC<GenericTextFieldProps> = ({
  name,
  label,
  disabled = false,
  multiline = false,
  rows = 1,
  placeholder,
  tooltip,
  type = "text",
  required = false,
  size = "small",
}) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<any>();

  // Helper function to get nested values safely
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Helper function to safely convert error to string
  const safeErrorToString = (error: any): string => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(', ');
    }
    return '';
  };

  const fieldValue = getNestedValue(values, name) || "";
  const fieldError = getNestedValue(errors, name);
  const fieldTouched = getNestedValue(touched, name);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(name, event.target.value);
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  const textField = (
    <TextField
      fullWidth
      size={size}
      name={name}
      label={label}
      type={type}
      value={fieldValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      placeholder={placeholder}
      required={required}
      error={fieldTouched && Boolean(fieldError)}
      helperText={fieldTouched && fieldError ? safeErrorToString(fieldError) : ""}
      sx={{ mb: 2 }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <Box>{textField}</Box>
    </Tooltip>
  ) : (
    textField
  );
};

export default GenericTextField;
